import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import { supabase } from '../../lib/supabase';
import { Button } from '../../shared/ui/Button';
import { FormError } from '../../shared/ui/FormError';
import { Input } from '../../shared/ui/Input';
import { Label } from '../../shared/ui/Label';
import { PhoneField } from '../../shared/ui/PhoneField';
import { StatusBanner } from '../../shared/ui/StatusBanner';
import { Turnstile } from '../../shared/ui/Turnstile';

import { AuthDivider } from './AuthDivider';
import { AuthLayout } from './AuthLayout';
import { GoogleButton } from './GoogleButton';
import { signupSchema, type SignupInput } from './schemas';
import s from './SignupPage.module.css';

export function SignupPage(): JSX.Element {
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Supabase Auth verifies this server-side when CAPTCHA protection is enabled
  // in the dashboard (Authentication -> Settings, provider Turnstile). We do
  // not verify it ourselves: signUp goes straight to GoTrue, so routing it
  // through an Edge Function purely to check a token would put a hop in front
  // of every signup for no additional guarantee.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const onToken = useCallback((token: string | null) => {
    setCaptchaToken(token);
  }, []);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignupInput>({
    resolver: zodResolver(signupSchema),
    defaultValues: { name: '', email: '', phone: '', password: '' },
  });

  async function onSubmit(values: SignupInput): Promise<void> {
    setSubmitError(null);
    const { error } = await supabase.auth.signUp({
      email: values.email,
      password: values.password,
      options: {
        data: {
          name: values.name,
          phone: values.phone,
        },
        ...(captchaToken === null ? {} : { captchaToken }),
      },
    });

    if (error !== null) {
      setSubmitError(error.message);
      return;
    }
    // Phase 184: email confirmation replaced the phone-OTP gate. With "Confirm
    // email" on, signUp returns no session — the account exists but is not
    // usable until the link is opened — so the only honest next screen is
    // "check your inbox". The address travels in router state rather than the
    // URL so it stays out of history, Referer headers and path analytics.
    navigate('/check-inbox', { replace: true, state: { email: values.email } });
  }

  return (
    <AuthLayout
      pitchTitle={
        <>
          Start practising <em>like exam day.</em>
        </>
      }
      pitchBody="Create an account in under a minute. Pick a target band, choose academic or general, and start your first mock."
    >
      <span className={s.eyebrow}>Get started</span>
      <h1 className={s.title}>Create an account</h1>
      <p className={s.subtitle}>
        Already have one? <Link to="/login">Log in</Link>.
      </p>

      <GoogleButton label="Sign up" />
      <AuthDivider label="or sign up with email" />

      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(onSubmit)(event);
        }}
        className={s.form}
      >
        <div>
          <Label htmlFor="name">Full name</Label>
          <Input
            id="name"
            autoComplete="name"
            autoCapitalize="words"
            enterKeyHint="next"
            placeholder="Alice Sharma"
            invalid={errors.name !== undefined}
            aria-describedby={errors.name ? 'name-error' : undefined}
            {...register('name')}
          />
          <FormError id="name-error" message={errors.name?.message} />
        </div>

        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="next"
            placeholder="you@institute.edu"
            invalid={errors.email !== undefined}
            aria-describedby={errors.email ? 'email-error' : undefined}
            {...register('email')}
          />
          <FormError id="email-error" message={errors.email?.message} />
        </div>

        <div>
          <Label htmlFor="phone">Mobile number</Label>
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <PhoneField
                id="phone"
                value={field.value}
                onChange={field.onChange}
                invalid={errors.phone !== undefined}
                describedBy={errors.phone ? 'phone-error' : 'phone-help'}
              />
            )}
          />
          {errors.phone !== undefined ? (
            <FormError id="phone-error" message={errors.phone.message} />
          ) : (
            <p id="phone-help" className={s.hint}>
              <svg
                className={s.hintIcon}
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="6" cy="6" r="4.5" />
                <path d="M6 4.5v1.5M6 8h.01" />
              </svg>
              Pick your country, then type the number without the leading zero.
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            enterKeyHint="go"
            placeholder="At least 8 characters"
            invalid={errors.password !== undefined}
            aria-describedby={errors.password ? 'password-error' : undefined}
            {...register('password')}
          />
          <FormError id="password-error" message={errors.password?.message} />
        </div>

        {submitError !== null ? <StatusBanner tone="error">{submitError}</StatusBanner> : null}

        {/* Renders nothing unless a site key is configured. */}
        <Turnstile onToken={onToken} />

        <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
          Create account
        </Button>

        <p className={s.legal}>
          By creating an account you agree to verify your phone via OTP before accessing your
          workspace.
        </p>
      </form>
    </AuthLayout>
  );
}
